// src/admin/EarlyAttendanceStats.js
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import { firestore } from '../firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import './EarlyAttendanceStats.css';

const EarlyAttendanceStats = () => {
  const { allStudentsForOutlet, selectedClassForOutlet } = useOutletContext();
  const { classMap, currentYearId } = useContext(StudentContext);

  const allStudents = allStudentsForOutlet || [];

  const [attendanceMap, setAttendanceMap] = useState({});
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  // Bộ lọc ngày & tìm kiếm
  const [earlyAttendanceDateRange, setEarlyAttendanceDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Sắp xếp cột: key & direction ('asc' | 'desc')
  const [sortConfig, setSortConfig] = useState({ key: 'both', direction: 'desc' });

  // Tải dữ liệu điểm danh từ Firestore theo Niên khóa hiện tại
  useEffect(() => {
    const fetchStudentAttendances = async () => {
      if (!currentYearId) return;
      setLoadingAttendance(true);
      try {
        const attColRef = collection(firestore, `academic_years/${currentYearId}/student_attendances`);
        const snap = await getDocs(attColRef);

        const map = {};
        snap.docs.forEach((d) => {
          const data = d.data();
          map[d.id] = data.church || {};
        });

        setAttendanceMap(map);
      } catch (err) {
        console.error('Lỗi khi nạp dữ liệu điểm danh từ Firestore:', err);
      } finally {
        setLoadingAttendance(false);
      }
    };

    fetchStudentAttendances();
  }, [currentYearId]);

  const handleEarlyAttendanceDateChange = (e) => {
    setEarlyAttendanceDateRange({
      ...earlyAttendanceDateRange,
      [e.target.name]: e.target.value
    });
  };

  // 1. Tính toán trước số buổi đi lễ và tổng hợp theo từng thiếu nhi
  const processedStudents = useMemo(() => {
    const startDate = earlyAttendanceDateRange.startDate ? new Date(earlyAttendanceDateRange.startDate) : null;
    const endDate = earlyAttendanceDateRange.endDate ? new Date(earlyAttendanceDateRange.endDate) : null;

    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(23, 59, 59, 999);

    return allStudents.map((student) => {
      let sundayEarlyCount = 0;
      let thursdayCount = 0;

      const churchAttendance = attendanceMap[student.id] || {};

      Object.keys(churchAttendance).forEach((dateKey) => {
        const record = churchAttendance[dateKey];
        if (!record || !record.time || !record.time.includes('Có lúc')) return;

        const [year, month, day] = dateKey.split('-').map(Number);
        if (!year || !month || !day) return;

        const dateObj = new Date(year, month - 1, day);
        if (isNaN(dateObj.getTime())) return;

        if (startDate && dateObj < startDate) return;
        if (endDate && dateObj > endDate) return;

        const dayOfWeek = dateObj.getDay(); // 0: Chúa Nhật, 4: Thứ Năm
        const isSunday = dayOfWeek === 0;
        const isThursday = dayOfWeek === 4;

        const timeStrMatch = record.time.match(/(\d{1,2}):(\d{2})/);
        if (!timeStrMatch) return;

        const hours = parseInt(timeStrMatch[1], 10);
        const minutes = parseInt(timeStrMatch[2], 10);

        // Đi lễ sớm trước hoặc đúng 7:40 sáng Chúa Nhật
        const isEarly = hours < 7 || (hours === 7 && minutes <= 40);

        if (isSunday && isEarly) sundayEarlyCount++;
        if (isThursday) thursdayCount++;
      });

      const className = student.className || classMap[student.classId] || student.classId || 'Chưa xếp lớp';

      return {
        ...student,
        className,
        sundayEarlyCount,
        thursdayCount,
        bothCount: sundayEarlyCount + thursdayCount
      };
    });
  }, [allStudents, attendanceMap, earlyAttendanceDateRange, classMap]);

  // 2. Lọc theo ô tìm kiếm & Sắp xếp đa chiều (Hỗ trợ sắp xếp CN và T5 cùng lúc)
  const filteredAndSortedStudents = useMemo(() => {
    let list = processedStudents.filter((student) => {
      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase().trim();
      const holyName = (student.holyName || '').toLowerCase();
      const fullName = (student.fullName || '').toLowerCase();
      const id = (student.id || '').toLowerCase();
      const className = (student.className || '').toLowerCase();

      return (
        id.includes(term) ||
        holyName.includes(term) ||
        fullName.includes(term) ||
        className.includes(term)
      );
    });

    if (sortConfig.key) {
      list.sort((a, b) => {
        const dir = sortConfig.direction === 'asc' ? 1 : -1;

        // 🌟 SẮP XẾP CẢ CHÚA NHẬT VÀ THỨ NĂM CÙNG LÚC
        if (sortConfig.key === 'both') {
          if (a.bothCount !== b.bothCount) {
            return (a.bothCount - b.bothCount) * dir;
          }
          if (a.sundayEarlyCount !== b.sundayEarlyCount) {
            return (a.sundayEarlyCount - b.sundayEarlyCount) * dir;
          }
          return (a.thursdayCount - b.thursdayCount) * dir;
        }

        // Sắp xếp Chúa Nhật (Tự động tie-break bằng Thứ Năm cùng lúc)
        if (sortConfig.key === 'sundayEarlyCount') {
          if (a.sundayEarlyCount !== b.sundayEarlyCount) {
            return (a.sundayEarlyCount - b.sundayEarlyCount) * dir;
          }
          return (a.thursdayCount - b.thursdayCount) * dir;
        }

        // Sắp xếp Thứ Năm (Tự động tie-break bằng Chúa Nhật cùng lúc)
        if (sortConfig.key === 'thursdayCount') {
          if (a.thursdayCount !== b.thursdayCount) {
            return (a.thursdayCount - b.thursdayCount) * dir;
          }
          return (a.sundayEarlyCount - b.sundayEarlyCount) * dir;
        }

        // Sắp xếp theo Tên (từ cuối cùng)
        if (sortConfig.key === 'fullName') {
          const getLastName = (name) => (name || '').trim().split(/\s+/).pop().toLowerCase();
          const nameA = getLastName(a.fullName);
          const nameB = getLastName(b.fullName);
          return nameA.localeCompare(nameB, 'vi') * dir;
        }

        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];

        if (typeof valA === 'string' || typeof valB === 'string') {
          return String(valA || '').localeCompare(String(valB || ''), 'vi') * dir;
        }

        return ((valA || 0) - (valB || 0)) * dir;
      });
    }

    return list;
  }, [processedStudents, searchTerm, sortConfig]);

  // Xử lý đổi hướng sắp xếp khi click tiêu đề
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Xuất Excel
  const handleExportEarlyAttendanceToExcel = () => {
    if (filteredAndSortedStudents.length === 0) {
      alert('Không có dữ liệu để xuất Excel.');
      return;
    }

    const header = [
      'STT',
      'Mã ID',
      'Tên Thánh',
      'Họ và Tên',
      'Lớp',
      'Chúa Nhật đi lễ sớm (Trước 7:40)',
      'Đi lễ Thứ năm',
      'Tổng cộng (CN + T5)'
    ];

    const data = filteredAndSortedStudents.map((student, idx) => [
      idx + 1,
      student.id,
      student.holyName || '',
      student.fullName || '',
      student.className || '',
      student.sundayEarlyCount,
      student.thursdayCount,
      student.bothCount
    ]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    XLSX.utils.book_append_sheet(wb, ws, 'ThongKeDiLe');

    const className =
      selectedClassForOutlet === 'all' || !classMap[selectedClassForOutlet]
        ? 'TatCaLop'
        : classMap[selectedClassForOutlet].replace(/\s+/g, '_');

    XLSX.writeFile(wb, `ThongKe_DiLeSom_${className}.xlsx`);
  };

  return (
    <div className="eas-container">
      <h2 className="eas-title">Thống kê thiếu nhi đi lễ sớm (Trước 7:40 sáng Chúa Nhật)</h2>

      {/* THANH CÔNG CỤ BỘ LỌC & TÌM KIẾM */}
      <div className="eas-toolbar-card">
        <div className="eas-search-box">
          <input
            type="text"
            placeholder="🔎 Tìm kiếm theo ID, Tên Thánh, Họ và Tên, Lớp..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="eas-search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="eas-btn-clear-search" title="Xóa tìm kiếm">
              ✕
            </button>
          )}
        </div>

        <div className="eas-date-group">
          <label className="eas-date-label">
            <span>Từ:</span>
            <input
              type="date"
              name="startDate"
              value={earlyAttendanceDateRange.startDate}
              onChange={handleEarlyAttendanceDateChange}
              className="eas-date-input"
            />
          </label>
          <label className="eas-date-label">
            <span>Đến:</span>
            <input
              type="date"
              name="endDate"
              value={earlyAttendanceDateRange.endDate}
              onChange={handleEarlyAttendanceDateChange}
              className="eas-date-input"
            />
          </label>
        </div>

        <div className="eas-actions-group">
          {/* Nút bấm nhanh sắp xếp cả CN & T5 cùng lúc */}
          <button
            onClick={() => handleSort('both')}
            className={`eas-btn-sort-both ${sortConfig.key === 'both' ? 'active' : ''}`}
            title="Sắp xếp ưu tiên cả Chúa Nhật và Thứ Năm cùng lúc"
          >
            ⚡ Sắp xếp cả CN & T5 {sortConfig.key === 'both' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
          </button>

          <button
            onClick={handleExportEarlyAttendanceToExcel}
            className="eas-btn-export"
            disabled={loadingAttendance}
          >
            📥 Xuất Excel ({filteredAndSortedStudents.length})
          </button>
        </div>
      </div>

      {/* BẢNG THỐNG KÊ */}
      <div className="eas-table-wrapper">
        <table className="eas-table">
          <thead>
            <tr>
              <th className="eas-th eas-text-center" style={{ width: '45px' }}>STT</th>
              <th className="eas-th eas-sortable" onClick={() => handleSort('id')}>
                Mã ID <span className="eas-sort-icon">{getSortIcon('id')}</span>
              </th>
              <th className="eas-th eas-sortable" onClick={() => handleSort('holyName')}>
                Tên Thánh <span className="eas-sort-icon">{getSortIcon('holyName')}</span>
              </th>
              <th className="eas-th eas-sortable" onClick={() => handleSort('fullName')}>
                Họ và Tên <span className="eas-sort-icon">{getSortIcon('fullName')}</span>
              </th>
              {/* 🌟 CỘT LỚP MỚI THÊM 🌟 */}
              <th className="eas-th eas-sortable" onClick={() => handleSort('className')}>
                Lớp <span className="eas-sort-icon">{getSortIcon('className')}</span>
              </th>
              <th className="eas-th eas-text-center eas-sortable" onClick={() => handleSort('sundayEarlyCount')}>
                Chúa Nhật đi lễ sớm (≤ 7:40) <span className="eas-sort-icon">{getSortIcon('sundayEarlyCount')}</span>
              </th>
              <th className="eas-th eas-text-center eas-sortable" onClick={() => handleSort('thursdayCount')}>
                Đi lễ Thứ năm <span className="eas-sort-icon">{getSortIcon('thursdayCount')}</span>
              </th>
              {/* 🌟 CỘT SẮP XẾP CẢ HAI CÙNG LÚC 🌟 */}
              <th className="eas-th eas-text-center eas-sortable eas-th-both" onClick={() => handleSort('both')}>
                Cả hai (CN & T5) <span className="eas-sort-icon">{getSortIcon('both')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loadingAttendance ? (
              <tr>
                <td colSpan="8" className="eas-td-empty">
                  ⏳ Đang tải và phân tích dữ liệu điểm danh...
                </td>
              </tr>
            ) : filteredAndSortedStudents.length === 0 ? (
              <tr>
                <td colSpan="8" className="eas-td-empty">
                  {searchTerm
                    ? `Không tìm thấy thiếu nhi nào khớp với "${searchTerm}".`
                    : 'Không có dữ liệu thiếu nhi trong lớp được chọn.'}
                </td>
              </tr>
            ) : (
              filteredAndSortedStudents.map((student, index) => (
                <tr key={student.id} className={index % 2 === 0 ? 'eas-tr-even' : 'eas-tr-odd'}>
                  <td className="eas-td eas-text-center">{index + 1}</td>
                  <td className="eas-td eas-id-code">{student.id}</td>
                  <td className="eas-td">{student.holyName || '-'}</td>
                  <td className="eas-td eas-font-bold">{student.fullName || ''}</td>
                  <td className="eas-td">
                    <span className="eas-class-badge">{student.className}</span>
                  </td>
                  <td className="eas-td eas-text-center eas-badge-present">
                    {student.sundayEarlyCount}
                  </td>
                  <td className="eas-td eas-text-center eas-badge-thursday">
                    {student.thursdayCount}
                  </td>
                  <td className="eas-td eas-text-center eas-badge-both">
                    <strong>{student.bothCount}</strong>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EarlyAttendanceStats;